import { useEffect, useMemo, useState } from 'react'
import { Loader2, X, Save, Clipboard, ClipboardCheck } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { getKnowledgeJson, saveKnowledgeJson } from '@/api/kuizy'

export default function KnowledgeJsonModal({ visible, onClose, fileInfo }) {
  const [title, setTitle] = useState('')
  const [desc, setDesc] = useState('')
  const [summary, setSummary] = useState('')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [copied, setCopied] = useState(false)
  const [orig, setOrig] = useState({ title: '', desc: '', summary: '' })

  const dirty = useMemo(() => (
    title !== orig.title || desc !== orig.desc || summary !== orig.summary
  ), [title, desc, summary, orig])

  useEffect(() => {
    if (!visible || !fileInfo) return
    const { eid, fid } = fileInfo
    setLoading(true)
    getKnowledgeJson(eid, fid)
      .then(({ data }) => {
        const t = data.title ?? ''
        const d = data.description ?? ''
        const s = data.summary ?? ''
        setTitle(t)
        setDesc(d)
        setSummary(s)
        setOrig({ title: t, desc: d, summary: s })
      })
      .finally(() => setLoading(false))
  }, [visible, fileInfo])

  useEffect(() => {
    if (!visible) return
    const onKey = (e) => {
      const mod = e.metaKey || e.ctrlKey
      if (e.key === 'Escape') onClose?.({ saved: false })
      if (mod && e.key.toLowerCase() === 's') {
        e.preventDefault()
        if (!saving && dirty) handleSave()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [visible, saving, dirty, title, desc, summary])

  const copySummary = async () => {
    try {
      await navigator.clipboard.writeText(summary || '')
      setCopied(true)
      setTimeout(() => setCopied(false), 1200)
    } catch {}
  }

  const handleSave = async () => {
    const { uid, eid, fid } = fileInfo
    setSaving(true)
    await saveKnowledgeJson({
      user_id: uid,
      exam_id: eid,
      id: fid,
      title,
      description: desc,
      summary
    })
    setSaving(false)
    setOrig({ title, desc, summary })
    onClose({
      saved: true,
      meta: { fid, title, description: desc, summary }
    })
  }

  if (!visible) return null

  const Stat = ({ label, value }) => (
    <span className="inline-flex items-center gap-1 text-[11px] text-gray-500">
      <span className="font-medium">{label}:</span> {value}
    </span>
  )

  return (
    <AnimatePresence>
      <motion.div
        key="kj-ovl"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 flex items-center justify-center z-[1100]"
        onClick={() => onClose({ saved: false })}
      >
        <motion.div
          key="kj-mdl"
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.97, opacity: 0, y: 10 }}
          transition={{ duration: 0.22 }}
          className="bg-white rounded-2xl w-[92%] max-w-[960px] h-[85vh] flex flex-col overflow-hidden"
          onClick={e => e.stopPropagation()}
        >
          <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50">
            <h2 className="text-base font-semibold">
              แก้ไขไฟล์ <span className="text-primary">{fileInfo?.fid}</span>
            </h2>
            <div className="flex items-center gap-2">
              <button
                className="inline-flex items-center gap-1 bg-primary text-white rounded-md px-3 py-1.5 text-xs font-semibold disabled:opacity-60"
                disabled={loading || saving || !dirty}
                onClick={handleSave}
                title="บันทึก (⌘/Ctrl + S)"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} บันทึก
              </button>
              <button className="text-gray-600 hover:text-primary" onClick={() => onClose({ saved: false })} title="ปิด (Esc)">
                <X size={18} />
              </button>
            </div>
          </div>

          <div className="flex-1 flex flex-col min-h-0">
            {loading ? (
              <div className="flex-1 flex items-center justify-center text-gray-500 text-sm">
                <Loader2 className="animate-spin mr-2" size={16} /> กำลังโหลด…
              </div>
            ) : (
              <div className="flex flex-col gap-4 flex-1 min-h-0 px-6 py-6 overflow-hidden">
                <label className="text-sm flex flex-col gap-1">
                  <span className="font-medium">ชื่อไฟล์ (Title)</span>
                  <input
                    value={title}
                    onChange={e => setTitle(e.target.value)}
                    placeholder="ตั้งชื่อที่สื่อความหมาย เช่น 'บทที่ 1 ระบบนิเวศ'"
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary"
                  />
                  <div className="flex gap-3">
                    <Stat label="ตัวอักษร" value={title.length} />
                  </div>
                </label>

                <label className="text-sm flex flex-col gap-1">
                  <span className="font-medium">รายละเอียด (Description)</span>
                  <textarea
                    rows={3}
                    value={desc}
                    onChange={e => setDesc(e.target.value)}
                    placeholder="อธิบายย่อ ว่าไฟล์นี้ครอบคลุมเรื่องใด ใช้ภาษาที่เข้าใจง่าย"
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary resize-y"
                  />
                  <div className="flex gap-3">
                    <Stat label="ตัวอักษร" value={desc.length} />
                    <Stat label="คำ" value={(desc.trim() ? desc.trim().split(/\s+/).length : 0)} />
                  </div>
                </label>

                <label className="text-sm flex flex-col gap-1 flex-1 min-h-0">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">สรุป / เนื้อหา (Summary)</span>
                    <button
                      type="button"
                      onClick={copySummary}
                      className="inline-flex items-center gap-1 text-xs bg-white border border-gray-300 hover:bg-gray-50 rounded-md px-2 py-1"
                      title="คัดลอกสรุป"
                    >
                      {copied ? <ClipboardCheck size={14} /> : <Clipboard size={14} />} {copied ? 'คัดลอกแล้ว' : 'คัดลอก'}
                    </button>
                  </div>
                  <textarea
                    value={summary}
                    onChange={e => setSummary(e.target.value)}
                    placeholder="ปรับแก้สรุปให้อ่านง่ายและเหมาะสำหรับสร้างข้อสอบ"
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary flex-1 min-h-0 resize-none overflow-auto"
                  />
                  <div className="flex gap-3">
                    <Stat label="ตัวอักษร" value={summary.length} />
                    <Stat label="คำ" value={(summary.trim() ? summary.trim().split(/\s+/).length : 0)} />
                  </div>
                </label>
              </div>
            )}
          </div>

          <div className="px-6 py-4 border-t bg-gray-50 flex justify-end">
            <button
              disabled={loading || saving || !dirty}
              onClick={handleSave}
              className="bg-primary hover:bg-secondary disabled:opacity-60 text-white rounded-lg px-5 py-2 text-sm font-semibold"
              title="บันทึก (⌘/Ctrl + S)"
            >
              {saving ? 'กำลังบันทึก…' : 'บันทึก'}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}
